import React, { useState } from 'react';
import { Package, Truck, MapPin, Clock, CheckCircle2, Phone, Mail, ArrowRight, Globe, Shield, DollarSign } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useToast } from '../hooks/use-toast';
import { Helmet } from 'react-helmet-async';

const ShippingToIndiaPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    weight: ''
  });
  const { toast } = useToast();

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    toast({
      title: "Quote Request Received!",
      description: "Our team will contact you within 24 hours with shipping rates to India."
    });
    setFormData({ name: '', email: '', phone: '', weight: '' });
  };

  const benefits = [
    {
      icon: DollarSign,
      title: "Competitive Rates",
      description: "Save up to 75% compared to standard courier rates. Shipping options from $7.99/kg for bulk shipments."
    },
    {
      icon: Truck,
      title: "Major Carrier Partners",
      description: "We work with DHL, FedEx, UPS, and Purolator to ensure fast and reliable delivery to India."
    },
    {
      icon: MapPin,
      title: "Door-to-Door Service",
      description: "Convenient pickup from your location in Canada and direct delivery to any address in India."
    },
    {
      icon: Package,
      title: "Real-Time Tracking",
      description: "Track your shipment every step of the way with live updates from pickup to delivery."
    },
    {
      icon: Shield,
      title: "Customs Assistance",
      description: "Expert support with customs documentation and clearance to ensure smooth delivery."
    },
    {
      icon: Globe,
      title: "Relocation Friendly",
      description: "Perfect for personal parcels, gifts, and large relocation shipments to India."
    }
  ];

  const steps = [
    {
      number: 1,
      title: "Book Your Shipment",
      description: "Contact us online or visit one of our partner drop-off locations across Canada."
    },
    {
      number: 2,
      title: "Weighing & Measurement",
      description: "We calculate actual and dimensional weight to provide the most accurate pricing."
    },
    {
      number: 3,
      title: "Carrier Selection",
      description: "We compare DHL, FedEx, UPS rates and select the best option for speed and price."
    },
    {
      number: 4,
      title: "Shipment Dispatch",
      description: "Your parcel is dispatched with full tracking information sent to your email."
    },
    {
      number: 5,
      title: "Delivery in India",
      description: "Door-to-door delivery to any city in India, with customs clearance support included."
    }
  ];

  const allowedItems = [
    'Personal belongings and clothing',
    'Gifts and electronics',
    'Documents and books',
    'Household items for relocation',
    'Non-perishable food items',
    'Shoes and accessories'
  ];

  const canadianCities = [
    { name: 'Toronto', province: 'Ontario' },
    { name: 'Brampton', province: 'Ontario' },
    { name: 'Mississauga', province: 'Ontario' },
    { name: 'Etobicoke', province: 'Ontario' },
    { name: 'Surrey', province: 'British Columbia' },
    { name: 'Vancouver', province: 'British Columbia' },
    { name: 'Halifax', province: 'Nova Scotia' },
    { name: 'Montreal', province: 'Quebec' }
  ];

  const faqs = [
    {
      question: "How long does shipping from Canada to India take?",
      answer: "Delivery times typically range from 5-10 business days depending on the carrier and service selected. Express services can deliver in 3-5 days, while economy options may take up to 15 days."
    },
    {
      question: "Which carriers does Shippzy use for shipping to India?",
      answer: "We partner with major international carriers including DHL, FedEx, UPS, and Purolator. We compare rates across all carriers to provide you with the best price and delivery time for your shipment."
    },
    {
      question: "Can Shippzy help with customs clearance in India?",
      answer: "Yes! We provide comprehensive customs documentation support and work with customs agents in India to ensure smooth clearance. However, any customs duties or taxes imposed by Indian authorities are the responsibility of the sender or recipient."
    },
    {
      question: "Do you provide door-to-door delivery in India?",
      answer: "Yes, we offer complete door-to-door service. We pick up from your location in Canada and deliver directly to the recipient's address anywhere in India."
    },
    {
      question: "What is the minimum weight for shipping to India?",
      answer: "There is no minimum weight for regular parcels. For relocation shipments with special rates from $7.99/kg (airport-to-airport, 100kg min), the minimum is 100 kg."
    },
    {
      question: "Are there items I cannot send to India from Canada?",
      answer: "Yes, prohibited items include liquids, flammable materials, weapons, cash, and certain electronics with lithium batteries. Please contact us for a complete list of restricted items."
    },
    {
      question: "How is shipping cost calculated?",
      answer: "Shipping costs are based on either actual weight or dimensional weight (Length × Width × Height in cm ÷ 5000), whichever is greater. We provide instant quotes based on your shipment details."
    },
    {
      question: "Can I track my shipment to India?",
      answer: "Absolutely! All shipments include real-time tracking. You'll receive a tracking number via email once your parcel is dispatched, allowing you to monitor its journey from Canada to India."
    }
  ];

  const [openFaq, setOpenFaq] = useState(null);

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };

  return (
    <>
      <Helmet>
        <title>Shipping to India from Canada | Affordable International Courier – Shippzy</title>
        <meta name="description" content="Send parcels from Canada to India with Shippzy. Affordable international courier services with DHL, FedEx, UPS and trusted carriers. Door-to-door delivery, customs clearance assistance and relocation shipping available." />
        <meta name="keywords" content="shipping to India from Canada, courier Canada to India, cheap shipping Canada to India, international shipping Canada to India, send parcel to India, Canada to India delivery, affordable shipping to India, door to door shipping India" />
        <link rel="canonical" href="https://www.shippzy.ca/shipping-to-india-from-canada" />
        
        {/* Open Graph */}
        <meta property="og:title" content="Shipping to India from Canada | Affordable Courier – Shippzy" />
        <meta property="og:description" content="Send parcels from Canada to India with Shippzy. Save up to 75% with DHL, FedEx, UPS. Door-to-door delivery, customs clearance assistance included." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.shippzy.ca/shipping-to-india-from-canada" />
        
        {/* Schema.org markup */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Service",
            "name": "Shipping to India from Canada",
            "provider": {
              "@type": "Organization",
              "name": "Shippzy Inc.",
              "telephone": "+1-437-616-0758",
              "email": "ship@shippzy.ca",
              "address": {
                "@type": "PostalAddress",
                "streetAddress": "140 Advance Blvd, Unit 7",
                "addressLocality": "Brampton",
                "addressRegion": "ON",
                "postalCode": "L6T 4Z8",
                "addressCountry": "CA"
              }
            },
            "areaServed": ["CA", "IN"],
            "serviceType": "International Shipping",
            "offers": {
              "@type": "Offer",
              "priceSpecification": {
                "@type": "PriceSpecification",
                "price": "9.99",
                "priceCurrency": "CAD",
                "unitText": "per kg",
                "minWeight": "23 kg minimum"
              }
            }
          })}
        </script>
        
        <script type="application/ld+json">
          {JSON.stringify(faqSchema)}
        </script>
      </Helmet>

      <div className="min-h-screen bg-white">
        {/* Hero Section */}
        <section className="bg-gradient-to-br from-gray-900 via-gray-800 to-black text-white py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl">
              <div className="inline-block bg-gradient-to-r from-red-600 to-yellow-500 text-white px-4 py-2 rounded-full text-sm font-semibold mb-6">
                ✈️ Fast & Affordable Shipping to India
              </div>
              <h1 className="text-5xl md:text-6xl font-bold mb-6 leading-tight">
                Shipping to India from Canada
              </h1>
              <p className="text-xl text-gray-300 leading-relaxed mb-8">
                Shippzy provides affordable and reliable parcel shipping from Canada to India with complete door-to-door service. We offer competitive rates through major carriers including DHL, FedEx, and UPS, along with pickup assistance, professional packaging support, expert customs clearance guidance, and timely delivery to any location across India.
              </p>
              <div className="flex flex-wrap gap-4">
                <Button 
                  className="bg-gradient-to-r from-red-600 to-yellow-500 hover:from-red-700 hover:to-yellow-600 text-white px-8 py-6 text-lg"
                  onClick={() => document.getElementById('quote-form').scrollIntoView({ behavior: 'smooth' })}
                >
                  Get Instant Quote
                </Button>
                <Button 
                  variant="outline" 
                  className="border-2 border-white text-white hover:bg-white hover:text-gray-900 px-8 py-6 text-lg"
                  onClick={() => window.location.href = 'tel:+14376160758'}
                >
                  Call: +1 (437) 616-0758
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* Why Choose Shippzy Section */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">Why Choose Shippzy for Shipping to India</h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                We make international shipping simple, affordable, and reliable with our comprehensive courier services from Canada to India.
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {benefits.map((benefit, index) => {
                const IconComponent = benefit.icon;
                return (
                  <Card key={index} className="hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                    <CardContent className="pt-6">
                      <div className="w-14 h-14 bg-gradient-to-r from-red-600 to-yellow-500 rounded-lg flex items-center justify-center mb-4">
                        <IconComponent className="w-7 h-7 text-white" />
                      </div>
                      <h3 className="text-xl font-semibold text-gray-900 mb-3">{benefit.title}</h3>
                      <p className="text-gray-600 leading-relaxed">{benefit.description}</p>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        </section>

        {/* How It Works Section */}
        <section className="py-20 bg-white">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">How the Shipping Process Works</h2>
              <p className="text-xl text-gray-600">Simple 5-step process to ship your parcels from Canada to India</p>
            </div>

            <div className="space-y-6">
              {steps.map((step, index) => (
                <Card key={index} className="border-2 hover:border-red-300 transition-colors">
                  <CardContent className="pt-6">
                    <div className="flex gap-6 items-start">
                      <div className="w-12 h-12 bg-gradient-to-r from-red-600 to-yellow-500 rounded-full flex items-center justify-center flex-shrink-0">
                        <span className="text-2xl font-bold text-white">{step.number}</span>
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-gray-900 mb-2">{step.title}</h3>
                        <p className="text-gray-600">{step.description}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* What Can You Send Section */}
        <section className="py-20 bg-gradient-to-br from-red-50 to-yellow-50">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">What Can You Send from Canada to India</h2>
              <p className="text-xl text-gray-600">
                We handle various types of shipments for personal and relocation purposes
              </p>
            </div>

            <Card className="shadow-xl">
              <CardContent className="pt-8">
                <div className="grid md:grid-cols-2 gap-4">
                  {allowedItems.map((item, index) => (
                    <div key={index} className="flex items-center gap-3">
                      <CheckCircle2 className="w-6 h-6 text-green-500 flex-shrink-0" />
                      <span className="text-gray-800">{item}</span>
                    </div>
                  ))}
                </div>
                <div className="mt-8 p-4 bg-yellow-100 border-l-4 border-yellow-500 rounded">
                  <p className="text-sm text-gray-800">
                    <strong>Note:</strong> Prohibited items include liquids, flammable materials, weapons, cash, and certain electronics. Contact us for a complete list of restricted items for shipping to India.
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* Cities We Serve Section */}
        <section className="py-20 bg-white">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">Cities We Serve in Canada</h2>
              <p className="text-xl text-gray-600">
                Pickup and drop-off services available across major Canadian cities
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {canadianCities.map((city, index) => (
                <Card key={index} className="text-center hover:shadow-lg transition-shadow">
                  <CardContent className="pt-6">
                    <MapPin className="w-8 h-8 text-red-600 mx-auto mb-3" />
                    <h3 className="text-lg font-bold text-gray-900">{city.name}</h3>
                    <p className="text-sm text-gray-600">{city.province}</p>
                  </CardContent>
                </Card>
              ))}
            </div>

            <div className="text-center mt-8">
              <p className="text-gray-600">
                Don't see your city? <a href="#contact" className="text-red-600 hover:text-red-700 font-semibold underline">Contact us</a> – we serve many more locations across Canada!
              </p>
            </div>

            <div className="mt-12 bg-gradient-to-r from-gray-50 to-gray-100 p-8 rounded-xl">
              <h3 className="text-2xl font-bold text-gray-900 mb-4 text-center">Related Services</h3>
              <div className="flex flex-wrap gap-4 justify-center">
                <a href="/cheap-shipping-canada-to-india" className="text-red-600 hover:text-red-700 font-semibold underline">
                  Cheap shipping options from Canada to India
                </a>
                <span className="text-gray-400">•</span>
                <a href="/relocation-shipping-canada-to-india" className="text-red-600 hover:text-red-700 font-semibold underline">
                  Relocation shipping to India
                </a>
                <span className="text-gray-400">•</span>
                <a href="/international-shipping-brampton" className="text-red-600 hover:text-red-700 font-semibold underline">
                  Shipping services in Brampton
                </a>
                <span className="text-gray-400">•</span>
                <a href="/international-courier-toronto" className="text-red-600 hover:text-red-700 font-semibold underline">
                  International courier services in Toronto
                </a>
              </div>
            </div>
          </div>
        </section>


        {/* Detailed Pricing Section */}
        <section className="py-20 bg-gradient-to-b from-white to-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold mb-6">Shipping Rates from Canada to India</h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Transparent pricing with no hidden fees. Applicable taxes charged separately.
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto mb-12">
              <Card className="border-t-4 border-red-600 hover:shadow-xl transition-all">
                <CardHeader><CardTitle className="text-2xl flex items-center gap-2"><DollarSign className="w-6 h-6 text-red-600" />Documents (Standard)</CardTitle></CardHeader>
                <CardContent><div className="text-3xl font-bold text-red-700 mb-2">CAD $44</div><div className="text-sm text-gray-600 flex items-center gap-2"><Clock className="w-4 h-4" />7-9 business days</div></CardContent>
              </Card>

              <Card className="border-t-4 border-red-600 hover:shadow-xl transition-all">
                <CardHeader><CardTitle className="text-2xl flex items-center gap-2"><DollarSign className="w-6 h-6 text-red-600" />Documents (Express)</CardTitle></CardHeader>
                <CardContent><div className="text-3xl font-bold text-red-700 mb-2">CAD $77</div><div className="text-sm text-gray-600 flex items-center gap-2"><Clock className="w-4 h-4" />2-5 business days</div></CardContent>
              </Card>

              <Card className="border-t-4 border-red-600 hover:shadow-xl transition-all">
                <CardHeader><CardTitle className="text-2xl flex items-center gap-2"><DollarSign className="w-6 h-6 text-red-600" />1 kg Parcel</CardTitle></CardHeader>
                <CardContent><div className="text-3xl font-bold text-red-700 mb-2">CAD $45</div><div className="text-sm text-gray-600 flex items-center gap-2"><Clock className="w-4 h-4" />5-7 business days</div></CardContent>
              </Card>

              <Card className="border-t-4 border-red-600 hover:shadow-xl transition-all">
                <CardHeader><CardTitle className="text-2xl flex items-center gap-2"><DollarSign className="w-6 h-6 text-red-600" />5 kg Parcel</CardTitle></CardHeader>
                <CardContent><div className="text-3xl font-bold text-red-700 mb-2">CAD $85</div><div className="text-sm text-gray-600 flex items-center gap-2"><Clock className="w-4 h-4" />5-7 business days</div></CardContent>
              </Card>

              <Card className="border-t-4 border-red-600 hover:shadow-xl transition-all">
                <CardHeader><CardTitle className="text-2xl flex items-center gap-2"><DollarSign className="w-6 h-6 text-red-600" />10 kg Parcel</CardTitle></CardHeader>
                <CardContent><div className="text-3xl font-bold text-red-700 mb-2">CAD $149</div><div className="text-sm text-gray-600 flex items-center gap-2"><Clock className="w-4 h-4" />5-7 business days</div></CardContent>
              </Card>

              <Card className="border-t-4 border-red-600 hover:shadow-xl transition-all">
                <CardHeader><CardTitle className="text-2xl flex items-center gap-2"><DollarSign className="w-6 h-6 text-red-600" />15 kg Parcel</CardTitle></CardHeader>
                <CardContent><div className="text-3xl font-bold text-red-700 mb-2">CAD $195</div><div className="text-sm text-gray-600 flex items-center gap-2"><Clock className="w-4 h-4" />5-7 business days</div></CardContent>
              </Card>

              <Card className="border-4 border-yellow-500 relative hover:shadow-xl transition-all">
                <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 bg-gradient-to-r from-yellow-500 to-orange-500 text-white px-6 py-1 rounded-full text-sm font-bold shadow-lg">BEST VALUE</div>
                <CardHeader><CardTitle className="text-2xl flex items-center gap-2"><DollarSign className="w-6 h-6 text-yellow-600" />23 kg Slab</CardTitle></CardHeader>
                <CardContent><div className="text-3xl font-bold text-yellow-700 mb-2">CAD $9.99/kg</div><div className="text-sm text-gray-600 flex items-center gap-2"><Clock className="w-4 h-4" />5-10 business days</div></CardContent>
              </Card>

              <Card className="border-4 border-green-500 relative hover:shadow-xl transition-all">
                <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 bg-gradient-to-r from-green-500 to-emerald-500 text-white px-6 py-1 rounded-full text-sm font-bold shadow-lg">POPULAR</div>
                <CardHeader><CardTitle className="text-2xl flex items-center gap-2"><DollarSign className="w-6 h-6 text-green-600" />30 kg Slab</CardTitle></CardHeader>
                <CardContent><div className="text-3xl font-bold text-green-700 mb-2">CAD $9.99/kg</div><div className="text-sm text-gray-600 flex items-center gap-2"><Clock className="w-4 h-4" />5-10 business days</div></CardContent>
              </Card>
            </div>

            <div className="bg-yellow-50 border-l-4 border-yellow-500 p-6 rounded-lg max-w-4xl mx-auto">
              <div className="flex items-start gap-4">
                <Package className="w-6 h-6 text-yellow-600 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="font-bold text-gray-900 text-lg mb-3">Heavy Parcel Pricing</h3>
                  <div className="space-y-2 text-gray-700 mb-3">
                    <p><strong>23 kg:</strong> CAD $229.77 (exact)</p>
                    <p><strong>30 kg:</strong> CAD $299.70 (exact)</p>
                    <p><strong>Additional Weight:</strong> CAD $15/kg</p>
                  </div>
                  <p className="text-sm text-gray-600">
                    Additional weight is charged at CAD $15/kg beyond the applicable weight slab. The 30 kg slab has its own base rate.
                  </p>
                  <p className="text-sm text-gray-600 mt-2">Base shipping prices. Applicable taxes and separately chargeable services extra. Customs duties not included.</p>
                </div>
              </div>
            </div>

            <div className="mt-8 bg-red-50 border border-red-200 rounded-lg p-6 max-w-4xl mx-auto">
              <h3 className="font-bold text-gray-900 mb-2">India Airport-to-Airport Service</h3>
              <p className="text-gray-700">Special rate of <strong>CAD $7.99/kg</strong> available for India airport-to-airport service (100 kg minimum). Contact Shippzy for eligibility and booking.</p>
            </div>
          </div>
        </section>

        {/* Pricing Section */}
        <section className="py-20 bg-gradient-to-br from-gray-900 via-gray-800 to-black text-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-4xl font-bold mb-6">Affordable International Shipping Rates</h2>
            <p className="text-xl text-gray-300 mb-8">
              Get the best rates for shipping to India from Canada
            </p>

            <Card className="bg-gray-800 border-gray-700 text-white">
              <CardContent className="pt-8">
                <div className="flex items-center justify-center gap-4 mb-6">
                  <DollarSign className="w-12 h-12 text-yellow-400" />
                  <div className="text-left">
                    <p className="text-sm text-gray-400">Starting from</p>
                    <p className="text-5xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-red-500 to-yellow-400">
                      $7.99/kg
                    </p>
                  </div>
                </div>
                <p className="text-gray-300 mb-6">
                  Rates depend on shipment weight, dimensions, destination city, and selected carrier. We compare DHL, FedEx, and UPS to ensure you get the most competitive price for your shipment to India.
                </p>
                <div className="grid md:grid-cols-2 gap-4 text-left">
                  <div className="bg-gray-700 p-4 rounded-lg">
                    <h4 className="font-semibold mb-2">Regular Parcels</h4>
                    <p className="text-sm text-gray-300">Competitive per-kg rates for parcels of any size</p>
                  </div>
                  <div className="bg-gray-700 p-4 rounded-lg">
                    <h4 className="font-semibold mb-2">Bulk Shipments</h4>
                    <p className="text-sm text-gray-300">Special rates for 100kg+ relocation shipments</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">Frequently Asked Questions</h2>
              <p className="text-xl text-gray-600">Everything you need to know about shipping to India from Canada</p>
            </div>

            <div className="space-y-4">
              {faqs.map((faq, index) => (
                <Card key={index} className="border-2 hover:border-red-300 transition-colors cursor-pointer" onClick={() => setOpenFaq(openFaq === index ? null : index)}>
                  <CardContent className="pt-6">
                    <div className="flex items-start justify-between">
                      <h3 className="text-lg font-semibold text-gray-900 pr-4">{faq.question}</h3>
                      <ArrowRight className={`w-5 h-5 text-red-600 flex-shrink-0 transition-transform ${openFaq === index ? 'rotate-90' : ''}`} />
                    </div>
                    {openFaq === index && (
                      <p className="text-gray-600 mt-4 leading-relaxed">{faq.answer}</p>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* CTA / Quote Form Section */}
        <section id="quote-form" className="py-20 bg-gradient-to-r from-red-600 to-yellow-500">
          <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center text-white mb-8">
              <h2 className="text-4xl font-bold mb-4">Ready to Ship to India?</h2>
              <p className="text-xl">Get your free shipping quote today or visit our partner locations across Canada</p>
            </div>

            <Card className="shadow-2xl">
              <CardHeader>
                <CardTitle className="text-2xl text-center">Get Your Free Quote</CardTitle>
                <CardDescription className="text-center">Fill out the form and we'll provide you with the best rates</CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Full Name *</label>
                    <Input
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      placeholder="Your name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Email Address *</label>
                    <Input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      placeholder="your@email.com"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Phone Number *</label>
                    <Input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                      placeholder="+1 (123) 456-7890"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Estimated Weight (kg)</label>
                    <Input
                      type="number"
                      name="weight"
                      value={formData.weight}
                      onChange={handleChange}
                      placeholder="10"
                    />
                  </div>
                  <Button
                    type="submit"
                    className="w-full bg-gradient-to-r from-red-600 to-yellow-500 hover:from-red-700 hover:to-yellow-600 text-white h-12 text-lg"
                  >
                    Get Quote Now
                  </Button>
                </form>
              </CardContent>
            </Card>

            <div className="mt-8 text-center text-white">
              <p className="text-lg mb-4">Or contact us directly:</p>
              <div className="flex flex-col md:flex-row gap-4 justify-center">
                <a href="tel:+14376160758" className="flex items-center justify-center gap-2 bg-white text-gray-900 px-6 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors">
                  <Phone className="w-5 h-5" />
                  +1 (437) 616-0758
                </a>
                <a href="mailto:ship@shippzy.ca" className="flex items-center justify-center gap-2 bg-white text-gray-900 px-6 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors">
                  <Mail className="w-5 h-5" />
                  ship@shippzy.ca
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Bottom CTA Section */}
        <section className="py-12 bg-gray-900 text-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h3 className="text-2xl font-bold mb-4">Start Shipping to India Today with Shippzy</h3>
            <p className="text-gray-300 mb-6">
              Affordable, reliable, and fast international courier service from Canada to India. Compare rates from DHL, FedEx, and UPS. Door-to-door delivery with customs clearance support.
            </p>
            <div className="flex flex-wrap gap-4 justify-center">
              <Button 
                className="bg-gradient-to-r from-red-600 to-yellow-500 hover:from-red-700 hover:to-yellow-600 text-white px-8 py-3"
                onClick={() => window.location.href = '/partner'}
              >
                Find Drop-Off Location
              </Button>
              <Button 
                variant="outline" 
                className="border-2 border-white text-white hover:bg-white hover:text-gray-900 px-8 py-3"
                onClick={() => window.location.href = '/'}
              >
                Learn More About Shippzy
              </Button>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default ShippingToIndiaPage;
